import { create } from 'zustand'
import { draftApi } from '@/api/draftApi'
import { ApiError } from '@/api/httpClient'
import type { Draft, DraftOp } from '@/api/types'
import { morphToast } from '@/components/ui/Toast'
import { editorStateToMetadata, metadataToEditorState } from '@/services/metadata/adapters'
import { arrayToKeyed, keyedToArray, type MetadataV2 } from '@/services/metadata/keyed'
import { applyDraftOps } from '@/services/realtime/ops'
import {
  filterOpsByTouched,
  peekTouchedKeys,
  resetTouchedKeys,
  restoreTouchedKeys,
  takeTouchedKeys,
} from '@/services/realtime/touched'
import { useDiagramStore } from '@/stores/diagramStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useTabViewStore } from '@/stores/tabViewStore'
import { useTabsStore } from '@/stores/tabsStore'
import { useViewportStore } from '@/stores/viewportStore'

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'
export type EditorMode = 'draft' | 'view' | 'scratch'

export interface RemoteTableEdit {
  actorName: string
  rev: number
}

export interface RealtimeActor {
  userId?: number
  name?: string
}

interface DraftStore {
  draft: Draft | null
  metadata: MetadataV2 | null
  baseCommitId: number | null
  rev: number
  mode: EditorMode
  viewingCommitId: number | null
  currentProjectId: number | null
  currentProjectName: string | null
  isDirty: boolean
  saveStatus: SaveStatus
  lastSavedAt: string | null
  autosaveEnabled: boolean
  autosaveSuppressed: boolean
  saveError: string | null
  remoteTableEdits: Record<string, RemoteTableEdit>
  /** Incrementa a cada alteração local; detecta edição durante o save em voo. */
  dirtySeq: number

  setCurrentProjectId: (id: number | null) => void
  setCurrentProjectName: (name: string | null) => void
  setMode: (mode: EditorMode, viewingCommitId?: number | null) => void
  loadDraft: (projectId: number) => Promise<Draft>
  hydrateRealtimeDraft: (draft: Draft) => void
  applyRealtimeOps: (rev: number, ops: DraftOp[], actor?: RealtimeActor | null) => void
  performSave: () => Promise<void>
  markDirty: () => void
  clearDraft: () => void
  setAutosave: (enabled: boolean) => void
}

export const useDraftStore = create<DraftStore>((set, get) => ({
  draft: null,
  metadata: null,
  baseCommitId: null,
  rev: 0,
  mode: 'draft',
  viewingCommitId: null,
  currentProjectId: null,
  currentProjectName: null,
  isDirty: false,
  saveStatus: 'idle',
  lastSavedAt: null,
  autosaveEnabled: true,
  autosaveSuppressed: false,
  saveError: null,
  remoteTableEdits: {},
  dirtySeq: 0,

  setCurrentProjectId(id) {
    set({ currentProjectId: id })
  },

  setCurrentProjectName(name) {
    set({ currentProjectName: name })
  },

  setMode(mode, viewingCommitId = null) {
    set({ mode, viewingCommitId })
  },

  async loadDraft(projectId) {
    const draft = await draftApi.get(projectId)
    const metadata = draft.metadata as MetadataV2
    resetTouchedKeys()
    set({
      draft,
      metadata,
      baseCommitId: draft.base_commit,
      rev: draft.rev,
      mode: 'draft',
      viewingCommitId: null,
      isDirty: false,
      saveStatus: 'idle',
      saveError: null,
      remoteTableEdits: {},
    })
    return draft
  },

  hydrateRealtimeDraft(draft) {
    const previousMirror = get().metadata
    const metadata = draft.metadata as MetadataV2
    set({
      draft,
      metadata,
      baseCommitId: draft.base_commit,
      rev: draft.rev,
      saveError: null,
    })
    rebaseCanvasOnMirror(previousMirror, metadata)
  },

  applyRealtimeOps(rev, ops, actor) {
    const { metadata, draft, mode, remoteTableEdits } = get()
    if (!metadata || mode !== 'draft') return
    const nextMetadata = applyDraftOps(metadata, ops)
    set({
      draft: draft ? { ...draft, metadata: nextMetadata, rev } : draft,
      metadata: nextMetadata,
      rev,
      remoteTableEdits: markRemoteTableEdits(remoteTableEdits, ops, rev, actor?.name ?? ''),
    })
    rebaseCanvasOnMirror(metadata, nextMetadata)
  },

  async performSave() {
    const { currentProjectId, isDirty, saveStatus, mode, metadata, rev } = get()
    if (!currentProjectId || !isDirty || saveStatus === 'saving' || mode !== 'draft' || !metadata) return

    const invalidTable = useDiagramStore
      .getState()
      .tables.find((table) => (table.cols?.length ?? 0) === 0)
    if (invalidTable) {
      morphToast.error(`A tabela "${invalidTable.name}" precisa ter pelo menos uma coluna antes de salvar.`)
      return
    }

    const nextMetadata = buildEditorMetadata()
    // Restringe o lote ao que foi alterado NESTA aba: sem isso o diff exprime
    // "deixe o servidor igual à minha tela" e apaga o trabalho concorrente.
    const touched = takeTouchedKeys()
    const ops = filterOpsByTouched(diffToOps(metadata, nextMetadata), touched)
    if (ops.length === 0) {
      set({ isDirty: false, saveStatus: 'saved', saveError: null })
      return
    }

    const seqAtRequest = get().dirtySeq
    set({ saveStatus: 'saving', saveError: null })
    try {
      const response = await applyOpsWithRevisionRetry(currentProjectId, rev, metadata, nextMetadata, ops, touched)
      const savedMetadata = response.metadata as MetadataV2
      const currentDraft = get().draft
      const editedWhileSaving = get().dirtySeq !== seqAtRequest
      set({
        draft: currentDraft ? { ...currentDraft, metadata: savedMetadata, rev: response.rev } : currentDraft,
        metadata: savedMetadata,
        rev: response.rev,
        isDirty: editedWhileSaving,
        saveStatus: 'saved',
        lastSavedAt: new Date().toISOString(),
        saveError: null,
      })
      // O que o usuário digitou durante o round-trip não está no que voltou do
      // servidor: recarregar o canvas aqui apagaria essas edições.
      if (editedWhileSaving) return
      const activeTabId = useTabsStore.getState().activeTabId
      if (activeTabId) useTabsStore.getState().markClean(activeTabId)
      set({ autosaveSuppressed: true })
      useDiagramStore.getState().loadDiagram(metadataToEditorState(keyedToArray(savedMetadata)).diagram)
      set({ autosaveSuppressed: false })
    } catch (err) {
      restoreTouchedKeys(touched)
      set({ saveStatus: 'error', saveError: errMsg(err) })
      throw err
    }
  },

  markDirty() {
    set({ isDirty: true, dirtySeq: get().dirtySeq + 1 })
  },

  clearDraft() {
    resetTouchedKeys()
    set({
      draft: null,
      metadata: null,
      baseCommitId: null,
      rev: 0,
      mode: 'draft',
      viewingCommitId: null,
      isDirty: false,
      saveStatus: 'idle',
      lastSavedAt: null,
      autosaveSuppressed: false,
      saveError: null,
      remoteTableEdits: {},
    })
  },

  setAutosave(enabled) {
    set({ autosaveEnabled: enabled })
  },
}))

/** Estado atual do editor no formato keyed, que é o que o diff compara. */
function buildEditorMetadata(): MetadataV2 {
  const diagram = useDiagramStore.getState()
  const viewport = useViewportStore.getState()
  const tabView = useTabViewStore.getState()
  const settings = useSettingsStore.getState()

  return arrayToKeyed(editorStateToMetadata({
    diagram: {
      tables: diagram.tables,
      rels: diagram.rels,
      groups: diagram.groups,
      defaults: diagram.defaults,
      projectName: diagram.projectName,
      dialect: diagram.dialect,
    },
    viewport: { sc: viewport.sc, tx: viewport.tx, ty: viewport.ty },
    tabView: { hiddenTableIds: tabView.hiddenTableIds },
    settings: {
      defaultLineMode: settings.defaultLineMode,
      relationshipAnimationEnabled: settings.relationshipAnimationEnabled,
      showGrid: settings.showGrid,
      themePreference: settings.themePreference,
      sbCollapsed: settings.sbCollapsed,
      defaults: settings.defaults,
    },
    project: {
      name: diagram.projectName,
      description: '',
      dialect: diagram.dialect,
    },
  }))
}

/**
 * Recarrega o canvas a partir do novo espelho REAPLICANDO por cima o que ainda
 * não foi salvo nesta aba. Antes o canvas era simplesmente descartado quando
 * havia edição local pendente (ou pior, sobrescrito por ela) — aqui as duas
 * pontas sobrevivem: a mudança remota entra e o trabalho local é rebaseado.
 */
function rebaseCanvasOnMirror(previousMirror: MetadataV2 | null, nextMirror: MetadataV2): void {
  let target = nextMirror
  const touched = peekTouchedKeys()

  if (previousMirror && touched.size > 0) {
    const pending = filterOpsByTouched(diffToOps(previousMirror, buildEditorMetadata()), touched)
    if (pending.length > 0) {
      try {
        target = applyDraftOps(nextMirror, pending)
      } catch {
        // Uma op pendente pode não se aplicar ao novo espelho — por exemplo, a
        // tabela que ela endereça foi removida do outro lado. Aí o remoto vence
        // em vez de deixar o canvas num estado inconsistente.
        target = nextMirror
      }
    }
  }

  useDraftStore.setState({ autosaveSuppressed: true })
  useDiagramStore.getState().loadDiagram(metadataToEditorState(keyedToArray(target)).diagram)
  useDraftStore.setState({ autosaveSuppressed: false })
}

/**
 * Ops de tabela (`table.upsert`/`remove`/`move`) endereçam a tabela em `id`;
 * ops de coluna/índice endereçam em `tableId`. As demais (rel, group, ordens
 * globais, project, settings) não são escopadas a uma tabela.
 */
export function collectTouchedTableIds(ops: DraftOp[]): string[] {
  const ids = new Set<string>()
  for (const op of ops) {
    const tableId = op.op.startsWith('table.') ? op.id : op.tableId
    if (typeof tableId === 'string' && tableId.length > 0) ids.add(tableId)
  }
  return [...ids]
}

function markRemoteTableEdits(
  current: Record<string, RemoteTableEdit>,
  ops: DraftOp[],
  rev: number,
  actorName: string,
): Record<string, RemoteTableEdit> {
  const touched = collectTouchedTableIds(ops)
  if (touched.length === 0) return current
  const next = { ...current }
  for (const tableId of touched) next[tableId] = { actorName, rev }
  return next
}

async function applyOpsWithRevisionRetry(
  projectId: number,
  baseRev: number,
  baseMetadata: MetadataV2,
  desiredMetadata: MetadataV2,
  ops: DraftOp[],
  touched: Set<string>,
) {
  try {
    return await draftApi.applyOps(projectId, { baseRev, ops }, { silent: true })
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 409 || err.code !== 'DRAFT_REVISION_CONFLICT') {
      throw err
    }

    const latestDraft = await draftApi.get(projectId)
    const latestMetadata = latestDraft.metadata as MetadataV2
    const rebasedOps = filterOpsByTouched(diffToOps(latestMetadata, desiredMetadata), touched)
    if (rebasedOps.length === 0) {
      return { rev: latestDraft.rev, metadata: latestMetadata }
    }

    // If the desired state was already based on the latest metadata there is no
    // point retrying the exact same write and masking a real server problem.
    if (JSON.stringify(latestMetadata) === JSON.stringify(baseMetadata)) {
      throw err
    }

    return draftApi.applyOps(projectId, { baseRev: latestDraft.rev, ops: rebasedOps }, { silent: true })
  }
}

export function diffToOps(current: MetadataV2, next: MetadataV2): DraftOp[] {
  const ops: DraftOp[] = []

  if (JSON.stringify(current.project) !== JSON.stringify(next.project)) {
    ops.push({ op: 'project.update', fields: next.project })
  }
  if (JSON.stringify(current.settings) !== JSON.stringify(next.settings)) {
    ops.push({ op: 'settings.update', fields: next.settings })
  }

  const currentTableIds = new Set(Object.keys(current.tables))
  const nextTableIds = new Set(Object.keys(next.tables))

  for (const tableId of currentTableIds) {
    if (!nextTableIds.has(tableId)) ops.push({ op: 'table.remove', id: tableId })
  }
  for (const tableId of next.tableOrder) {
    const nextTable = next.tables[tableId]
    const currentTable = current.tables[tableId]
    if (!currentTable || JSON.stringify(stripTableCollections(currentTable)) !== JSON.stringify(stripTableCollections(nextTable))) {
      ops.push({ op: 'table.upsert', id: tableId, fields: stripTableCollections(nextTable) })
    }
    if (JSON.stringify(current.layout.positions[tableId] ?? null) !== JSON.stringify(next.layout.positions[tableId] ?? null)) {
      const pos = next.layout.positions[tableId] ?? { x: 0, y: 0 }
      ops.push({ op: 'table.move', id: tableId, x: sanitizeCoordinate(pos.x), y: sanitizeCoordinate(pos.y) })
    }
    pushNestedOps(ops, currentTable?.cols ?? {}, nextTable.cols, 'column', tableId)
    pushOrderOp(ops, currentTable?.colOrder ?? [], nextTable.colOrder, 'columnOrder.set', tableId)
    pushNestedOps(ops, currentTable?.indexes ?? {}, nextTable.indexes, 'index', tableId)
    pushOrderOp(ops, currentTable?.indexOrder ?? [], nextTable.indexOrder, 'indexOrder.set', tableId)
  }
  pushOrderOp(ops, current.tableOrder, next.tableOrder, 'tableOrder.set')
  pushTopLevelMapOps(ops, current.relationships, next.relationships, 'rel')
  pushOrderOp(ops, current.relationshipOrder, next.relationshipOrder, 'relOrder.set')
  pushTopLevelMapOps(ops, current.groups, next.groups, 'group')
  pushOrderOp(ops, current.groupOrder, next.groupOrder, 'groupOrder.set')
  return coalesceOps(ops)
}

function pushNestedOps(
  ops: DraftOp[],
  current: Record<string, unknown>,
  next: Record<string, unknown>,
  entity: 'column' | 'index',
  tableId: string,
) {
  const currentIds = new Set(Object.keys(current))
  const nextIds = new Set(Object.keys(next))
  for (const id of currentIds) {
    if (!nextIds.has(id)) ops.push({ op: `${entity}.remove`, tableId, id })
  }
  for (const id of nextIds) {
    if (JSON.stringify(current[id] ?? null) !== JSON.stringify(next[id] ?? null)) {
      ops.push({ op: `${entity}.upsert`, tableId, id, fields: next[id] as Record<string, unknown> })
    }
  }
}

function pushTopLevelMapOps(
  ops: DraftOp[],
  current: Record<string, unknown>,
  next: Record<string, unknown>,
  entity: 'rel' | 'group',
) {
  const currentIds = new Set(Object.keys(current))
  const nextIds = new Set(Object.keys(next))
  for (const id of currentIds) {
    if (!nextIds.has(id)) ops.push({ op: `${entity}.remove`, id })
  }
  for (const id of nextIds) {
    if (JSON.stringify(current[id] ?? null) !== JSON.stringify(next[id] ?? null)) {
      ops.push({ op: `${entity}.upsert`, id, fields: next[id] as Record<string, unknown> })
    }
  }
}

function pushOrderOp(
  ops: DraftOp[],
  current: string[],
  next: string[],
  op: 'tableOrder.set' | 'columnOrder.set' | 'indexOrder.set' | 'relOrder.set' | 'groupOrder.set',
  tableId?: string,
) {
  const sanitizedNext = next.filter((value): value is string => typeof value === 'string' && value.length > 0)
  if (JSON.stringify(current) === JSON.stringify(sanitizedNext)) return
  ops.push(tableId ? { op, tableId, order: sanitizedNext } : { op, order: sanitizedNext })
}

function stripTableCollections(table: MetadataV2['tables'][string]) {
  return {
    id: table.id,
    schema: table.schema,
    name: table.name,
    fullName: table.fullName,
    comment: table.comment,
    g: table.g,
    width: table.width,
  }
}

function coalesceOps(ops: DraftOp[]): DraftOp[] {
  const coalesced: DraftOp[] = []
  const byKey = new Map<string, DraftOp>()
  for (const op of ops) {
    const key = `${op.op}:${op.tableId ?? ''}:${op.id ?? ''}`
    if ((op.op.endsWith('.upsert') || op.op === 'project.update' || op.op === 'settings.update') && byKey.has(key)) {
      const previous = byKey.get(key)!
      previous.fields = { ...(previous.fields ?? {}), ...(op.fields ?? {}) }
      continue
    }
    byKey.set(key, op)
    coalesced.push(op)
  }
  return coalesced
}

function sanitizeCoordinate(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function errMsg(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Erro desconhecido'
}
